import React, {useState, useEffect, useRef} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {CardContent, Card} from '../../components/ui/card';
import BackButton from '../../components/ui/BackNavigationButton';
import NavigationButton from '../../components/ui/NavigationButton'; 
import WeeklySumForm from '../../components/forms/weeklySumForm/WeeklySumForm';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import { pages } from '../../components/utils/pages-links';
import TutorialOverlay from '../../components/ui/TutorialOverlay';
import { useAlert } from '../../context/alert/AlertProvider';
import { useTranslation } from 'react-i18next';
import ScenarioIndicator from '../../components/forms/scenario/ScenarioIndicator';
import useAutoSave from '../../hooks/useAutoSave';
import { Info, Play, X, Maximize2 } from 'lucide-react';

const VideoPopup = ({ isOpen, onClose, videoUrl, title = "Tutorial em Vídeo" }) => {
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    useEffect(() => {
        const handleEsc = (event) => {
            if (event.keyCode === 27) {
                onClose();
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    if (!isOpen) return null;

    return (
        <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
            onClick={onClose}
        >
            <div 
                className={`relative bg-white rounded-lg shadow-2xl ${
                    isFullscreen 
                        ? 'w-full h-full m-0' 
                        : 'w-full max-w-6xl mx-4 md:mx-8 max-h-[90vh]'
                } overflow-hidden`}
                onClick={e => e.stopPropagation()}
            >
                <div className="flex justify-between items-center p-4 border-b bg-gray-50">
                    <h3 className="text-lg font-semibold text-gray-900 truncate pr-4">
                        {title}
                    </h3>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={toggleFullscreen}
                            className="hidden md:flex p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
                            aria-label="Tela cheia"
                        >
                            <Maximize2 size={20} />
                        </button>
                        
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
                            aria-label="Fechar"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                <div className={`relative bg-black ${
                    isFullscreen 
                        ? 'h-[calc(100%-4rem)]' 
                        : 'h-[50vh] md:h-[60vh] lg:h-[70vh]'
                }`}>
                    {videoUrl ? (
                        <video
                            className="w-full h-full object-contain"
                            controls
                            preload="metadata"
                            playsInline
                            controlsList="nodownload"
                            style={{ backgroundColor: 'black' }}
                        >
                            <source src={videoUrl} type="video/mp4" />
                            <source src={videoUrl.replace('.mp4', '.webm')} type="video/webm" />
                            <p className="text-white p-4">
                                Seu navegador não suporta o elemento de vídeo.
                                <a href={videoUrl} className="text-blue-400 underline ml-2">
                                    Baixe o vídeo aqui
                                </a>
                            </p>
                        </video>
                    ) : (
                        <div className="flex items-center justify-center h-full text-white">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Play size={24} fill="white" />
                                </div>
                                <p className="text-lg">Vídeo não encontrado</p>
                                <p className="text-sm text-gray-300 mt-2">
                                    Verifique se o arquivo está na pasta public/videos/
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="md:hidden p-4 bg-gray-50 border-t">
                    <p className="text-sm text-gray-600 text-center">
                        Dica: Gire o dispositivo para uma melhor visualização
                    </p>
                </div>
            </div>
        </div>
    );
};

const VideoButton = ({ onClick, className = "", size = "default", variant = "primary",t }) => {
    const sizeClasses = {
        small: "px-3 py-2 text-sm",
        default: "px-4 py-2",
        large: "px-6 py-3 text-lg"
    };

    const variantClasses = {
        primary: "bg-indigo-600 hover:bg-indigo-700 text-white",
        secondary: "bg-gray-200 hover:bg-gray-300 text-gray-800",
        outline: "border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50"
    };

    return (
        <button
            onClick={onClick}
            className={`
                inline-flex items-center gap-2 rounded-lg font-medium transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                active:scale-95 shadow-sm hover:shadow-md
                ${sizeClasses[size]}
                ${variantClasses[variant]}
                ${className}
            `}
        >
            <Play size={size === "small" ? 16 : size === "large" ? 24 : 20} />
            <span className="hidden sm:inline">{t("form.weeklySum.viewTutorial")}</span>
            <span className="sm:hidden">Tutorial</span>
        </button>
    );
};

const WeeklySumPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [schedules, setSchedules] = useState([]);
    const [parameters, setParameters] = useState([]);
    const [showTutorial, setShowTutorial] = useState(false);
    const [tutorialStep, setTutorialStep] = useState(0);
    const [showVideoPopup, setShowVideoPopup] = useState(false);
    const { showAlert } = useAlert();
    const weeklySumFormRef = useRef(null);
    const weeklySumTableRef = useRef(null);
    const nextButtonRef = useRef(null);
    const isExampleMode = location.state?.isExample;
    const { t } = useTranslation();

    const videoUrl = "/videos/weekly-sum-tutorial.mp4";

    useAutoSave('weeklySumParams', parameters);

    const tutorialSteps = [
        {
            title: t("tutorial.weeklySum.title"),
            description: t("tutorial.weeklySum.description"),
            position: "center",
        },
        {
            title: t("tutorial.weeklySum.table.title"),
            description: t("tutorial.weeklySum.table.description"),
            position: "top-right",
            elementRef: weeklySumFormRef,
        },
        {
            title: t("tutorial.weeklySum.constraints.title"),
            description: t("tutorial.weeklySum.constraints.description"),
            position: "table",
            elementRef: weeklySumTableRef,  
        },
        {
            title: t("tutorial.weeklySum.next.title"),
            description: t("tutorial.weeklySum.next.description"),
            position: "next-button",
            elementRef: nextButtonRef,
        },
    ];

    useEffect(() => {
        if(isExampleMode){
            setShowTutorial(true);
        }

        const savedWeeklySumParams = JSON.parse(sessionStorage.getItem('weeklySumParams') || '[]');
        setParameters(savedWeeklySumParams);
        const savedSchedules = JSON.parse(sessionStorage.getItem('schedules') || '[]');
        setSchedules(savedSchedules);

    },[isExampleMode]);

    const handleAddParameter = (parameterData) => {
        try {
            if (Array.isArray(parameterData)) {
                setParameters(parameterData);
                return;
            }

            setParameters(prev => {
                const existingIndex = prev.findIndex(p => p.shift === parameterData.shift);
                if (existingIndex >= 0) {
                    const updated = [...prev];
                    updated[existingIndex] = parameterData;
                    return updated;
                } else {
                    return [...prev, parameterData];
                }
            });
        } catch (error) {
            console.error('⌛ Error in handleAddParameter:', error);
        }
    };

    const handleNext = () => {
        navigate('/form/generate',{
            state: {isExample: isExampleMode}
        });
    };

    const handleNextTutorialStep = () => {
        if (tutorialStep < tutorialSteps.length - 1){
          setTutorialStep(prev => prev + 1);
        } else {
          setShowTutorial(false);
        }
    };

    const handleBack = () => {
        navigate("/form/sseq", {
            state: { isExample: isExampleMode }
        });
    };

    return(
        <div className='-mt-16'>
            <Breadcrumbs pages={pages}/>
            <div className="p-6 bg-gray-100 min-h-screen">
                <div className='max-w-6xl mx-auto'>
                    <ScenarioIndicator />

                    <div className="mt-5 mb-6 bg-white rounded-lg shadow-sm p-4 border-l-4 border-indigo-500">
                        <div className="flex items-start gap-3">
                            <div className="text-indigo-500 mt-1">
                                <Info size={24} />
                            </div>
                            <div className="flex-1">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                    <div>
                                        <h2 className="text-lg font-medium text-gray-900 mb-1">
                                            {t("form.weeklySum.pageBannerTitle") || "Regras de Distribuição Semanal de Turnos"}
                                        </h2>
                                        <p className="text-gray-600">
                                            {t("form.weeklySum.pageBannerDescription") || 
                                            "Defina regras para quantos turnos de cada tipo devem ser atribuídos por semana. Por exemplo, pode querer que cada funcionário tenha pelo menos 2 dias de folga por semana, ou trabalhe entre 3-5 turnos da manhã por semana."}
                                        </p>
                                    </div>
                                    <VideoButton 
                                        onClick={() => setShowVideoPopup(true)}
                                        size="default"
                                        variant="primary"
                                        className="shrink-0"
                                        t={t}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <Card className="mb-6 mt-5">
                        <CardContent className="p-6">
                            <WeeklySumForm 
                                ref={weeklySumFormRef}
                                params={{}} 
                                schedules={schedules}
                                existingParameters={parameters}
                                onAddParameter={handleAddParameter}
                                isDisabled={isExampleMode}
                                translations={t}
                                isEditing={false}
                            />
                        </CardContent>
                    </Card>
    
                    <div className="flex justify-between items-center mt-6">
                        <BackButton onClick={handleBack}/>
                        <NavigationButton ref={nextButtonRef} onClick={handleNext}/>
                    </div>

                    {parameters.length === 0 && (
                        <Card className="mt-6 border border-gray-200">
                            <CardContent className="p-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-3">
                                    {t("form.weeklySum.examplesTitle") || "Exemplos de Regras de Distribuição Semanal"}
                                </h3>
                                <div className="space-y-4 text-gray-600 text-sm">
                                    <div className="p-3 bg-gray-50 rounded-lg">
                                        <p className="font-medium text-gray-800 mb-1">
                                            {t("form.weeklySum.example1Title") || "Garantir dias de descanso mínimos por semana:"}
                                        </p>
                                        <p>{t("form.weeklySum.example1Description") || 
                                           "Defina o Mínimo como 2 para turnos 'FF' (folgas) para garantir que cada funcionário tenha pelo menos 2 dias de descanso por semana."}</p>
                                    </div>
                                    <div className="p-3 bg-gray-50 rounded-lg">
                                        <p className="font-medium text-gray-800 mb-1">
                                            {t("form.weeklySum.example2Title") || "Equilibrar turnos da manhã entre o pessoal:"}
                                        </p>
                                        <p>{t("form.weeklySum.example2Description") || 
                                           "Defina o Ideal como 3-4 para turnos da manhã para garantir uma distribuição equilibrada entre os funcionários."}</p>
                                    </div>
                                    <div className="p-3 bg-gray-50 rounded-lg">
                                        <p className="font-medium text-gray-800 mb-1">
                                            {t("form.weeklySum.example3Title") || "Limitar turnos noturnos por razões de saúde:"}
                                        </p>
                                        <p>{t("form.weeklySum.example3Description") || 
                                           "Defina o Máximo como 3 para turnos noturnos para garantir que nenhum funcionário trabalhe mais de 3 turnos noturnos por semana."}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
            
            <TutorialOverlay 
                isVisible={showTutorial}
                onClose={() => setShowTutorial(false)}
                title={tutorialSteps[tutorialStep]?.title}
                description={tutorialSteps[tutorialStep]?.description}
                position={tutorialSteps[tutorialStep]?.position}
                targetRef={tutorialSteps[tutorialStep]?.elementRef}
                onNext={handleNextTutorialStep}
                isLastStep={tutorialStep === tutorialSteps.length - 1}
            />

            <VideoPopup
                isOpen={showVideoPopup}
                onClose={() => setShowVideoPopup(false)}
                videoUrl={videoUrl}
                title={t("form.weeklySum.videoTitle") || "Tutorial: Regras de Distribuição Semanal"}
            />
        </div>
    );
}

export default WeeklySumPage